import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { environment } from 'src/environments/environment';
import { EventTracker } from 'src/assets/data/enum/eventTracker';
import { FormsModule } from '@angular/forms';
import { EventTrackerService } from '../common/_services/eventTracker.service';
import { User } from '../common/_models/user';
import { UserService } from '../common/_services/user.service';
import { LegalsService } from '../common/_services/legals.service';
import { GeneralUtilsService } from '../common/_services/common/generalUtils.service';
import { setCurrentUser } from '../common/_actions/currentUser';
import { AppState } from '../common/_reducers';

export interface CookieConsentPreference {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

@Component({
  selector: 'app-cookie-banner',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: './cookie-banner.component.html',
  styleUrls: ['./cookie-banner.component.css'],
})
export class CookieBannerComponent {
  private eventTrackerService = inject(EventTrackerService);
  private translate = inject(TranslateService);
  private userService = inject(UserService);
  private legalsService = inject(LegalsService);
  private generalUtilsService = inject(GeneralUtilsService);

  showBanner = false;
  showManagePreference = false;
  consent: CookieConsentPreference;
  legalTemplate;
  currentUser: User;
  environment = environment;
  trackingProductValue: boolean | null = null;
  trackingMarketingValue: boolean | null = null;

  constructor(private store: Store<AppState>) {
    this.userService.getUserMeFromLocalStore().subscribe(async (u: User) => {
      this.currentUser = u;
      this.legalTemplate = await this.legalsService.getLegalTemplateOptionNoNext('it').toPromise();
      if (this.currentUser != null && this.currentUser.id != null) {
        if (this.currentUser?.legalLog?.length > 0) {
          const result = this.getLatestTrackingValues(this.currentUser?.legalLog);
          this.trackingProductValue = result.trackingProduct;
          this.trackingMarketingValue = result.trackingMarketing;
          if (this.trackingProductValue == null || this.trackingMarketingValue == null) {
            this.consent = {
              necessary: true,
              analytics: false,
              marketing: false,
            };
            this.showBanner = true;
          } else {
            this.activateEventTracking();
            this.showBanner = false;
          }
        } else {
          this.consent = {
            necessary: true,
            analytics: false,
            marketing: false,
          };
          this.showBanner = true;
        }
      }
    });
  }

  acceptAll() {
    this.consent = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    this.savePreference();
    this.showBanner = false;
  }

  rejectAll() {
    this.consent = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    this.savePreference();
    this.showBanner = false;
  }

  acceptOnlyNecessary() {
    this.consent = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    this.savePreference();
    this.showBanner = false;
  }

  acceptSelected() {
    this.savePreference();
    this.showBanner = false;
  }

  managePreferences() {
    this.showManagePreference = true;
  }

  onClose() {
    this.savePreference();
    this.showManagePreference = false;
    this.showBanner = false;
  }

  getLatestTrackingValues(items) {
    const sorted = items
      .filter((item) =>
        ['TRACKING_PRODUCT', 'TRACKING_MARKETING'].includes(item.legalTemplateOptionType)
      )
      .sort(
        (a: any, b: any) =>
          Math.abs(Date.now() - new Date(a.date).getTime()) -
          Math.abs(Date.now() - new Date(b.date).getTime())
      );

    return {
      trackingProduct: sorted.find(
        (item: any) => item.legalTemplateOptionType === 'TRACKING_PRODUCT'
      )?.value,
      trackingMarketing: sorted.find(
        (item: any) => item.legalTemplateOptionType === 'TRACKING_MARKETING'
      )?.value,
    };
  }

  savePreference() {
    const trackProduct = this.legalTemplate.find(
      (item: any) => item.optionType === 'TRACKING_PRODUCT'
    );
    const trackMarketing = this.legalTemplate.find(
      (item: any) => item.optionType === 'TRACKING_MARKETING'
    );

    const payloadItemP: any = {
      legalTemplateOption: trackProduct?.id,
      value: this.consent?.analytics,
      date: new Date(),
      user: this.currentUser.id,
      legalTemplate: trackProduct?.legalTemplate?.id,
    };

    const payloadItemM: any = {
      legalTemplateOption: trackMarketing?.id,
      value: this.consent?.marketing,
      date: new Date(),
      user: this.currentUser.id,
      legalTemplate: trackMarketing?.legalTemplate?.id,
    };

    const updatedUser: User = {
      ...this.currentUser,
      legalLog: [...(this.currentUser.legalLog ?? []), payloadItemP, payloadItemM],
    };

    this.userService.saveUser(updatedUser).subscribe((response) => {
      this.store.dispatch(setCurrentUser({ user: response }));
    });
  }

  activateEventTracking() {
    //initialize EventTracked tool
    const isLocalhost = window.location.hostname.includes('localhost');
    const { eventTrackingEnabled } = environment;
    if (!isLocalhost && eventTrackingEnabled == true) {
      if (this.trackingProductValue == false && this.trackingMarketingValue == false) {
        this.eventTrackerService.reset();
        return;
      }
      const userInfo = {
        email: this.currentUser.username,
        country: this.currentUser.preferredCountry,
        language: this.currentUser.preferredLanguage,
        tracking_product: this.trackingProductValue,
        tracking_marketing: this.trackingMarketingValue,
      };
      this.eventTrackerService.init();
      this.eventTrackerService.identifyUser(this.currentUser.id.toString(), userInfo);
      setTimeout(() => {
        this.eventTrackerService.trackEvent(EventTracker.OPEN_APPLICATION_WEB, null);
      }, 1000);
    }
  }

  goBack() {
    this.showManagePreference = false;
  }
}
